import { Drawer as BaseDrawer } from "@base-ui/react/drawer"
import { XMarkIcon } from "@heroicons/react/20/solid"
import { useIsDesktop } from "@src/hooks/useMediaQuery"
import { PortalContainerProvider } from "@src/hooks/usePortalContainer"
import clsx from "clsx"
import {
  type ComponentProps,
  type PropsWithChildren,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react"
import Button from "./Button"

export type DrawerProps = PropsWithChildren<{
  open: boolean
  onClose: () => void
  onCloseAnimationEnd?: () => void
  header?: ReactNode
  dismissible?: boolean
  hideContentOnNestedDrawer?: boolean
  forceDark?: boolean
  fullWidthContent?: boolean
  preserveHeight?: boolean
}>

const Drawer = ({
  open,
  onClose,
  onCloseAnimationEnd,
  header,
  children,
  dismissible = true,
  hideContentOnNestedDrawer = true,
  forceDark = false,
  fullWidthContent = false,
  preserveHeight = false,
}: DrawerProps) => {
  const isDesktop = useIsDesktop()
  const [isScrolled, setIsScrolled] = useState(false)
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(
    null
  )
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const handleScroll = () => {
    if (!scrollContainerRef.current) return
    setIsScrolled(scrollContainerRef.current.scrollTop > 0)
  }

  useEffect(() => {
    if (!open) setIsScrolled(false)
  }, [open])

  return (
    <BaseDrawer.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen || !open) return
        if (!dismissible) return
        onClose()
      }}
      onOpenChangeComplete={(nextOpen) => {
        if (!nextOpen) {
          onCloseAnimationEnd?.()
        }
      }}
      swipeDirection={isDesktop ? "right" : "down"}
    >
      <BaseDrawer.Portal className={clsx(forceDark && "dark")}>
        <BaseDrawer.Backdrop className="fixed inset-0 z-10 min-h-dvh bg-gray-900 opacity-[calc(var(--backdrop-opacity)*(1-var(--drawer-swipe-progress)))] transition-opacity duration-450 ease-[cubic-bezier(0.32,0.72,0,1)] [--backdrop-opacity:0.4] data-ending-style:opacity-0 data-starting-style:opacity-0 data-ending-style:duration-[calc(var(--drawer-swipe-strength)*450ms)] data-swiping:duration-0 supports-[-webkit-touch-callout:none]:absolute dark:bg-gray-950 xl:[--backdrop-opacity:0.2] dark:[--backdrop-opacity:0.8] xl:dark:[--backdrop-opacity:0.8]" />

        <BaseDrawer.Viewport className="fixed inset-0 z-20 flex items-end justify-center [--viewport-padding:0.5rem] xl:items-stretch xl:justify-end xl:p-(--viewport-padding) xl:pl-16">
          <BaseDrawer.Popup
            ref={setPortalContainer}
            className={clsx(
              // Common
              "group/popup -outline-offset-1 pointer-events-auto relative flex w-full touch-auto flex-col overflow-hidden overscroll-contain bg-white shadow-xl outline outline-gray-150 duration-450 ease-[cubic-bezier(0.32,0.72,0,1)] data-swiping:select-none data-ending-style:duration-[calc(var(--drawer-swipe-strength)*450ms)]",

              // Dark mode
              "dark:bg-gray-850 dark:shadow-none dark:outline-white/5",

              // Mobile (bottom sheet)
              "-mb-(--drawer-bleed) transform-[translateY(calc(var(--drawer-swipe-movement-y)-var(--stack-peek-offset)-(var(--shrink)*var(--stack-height))))_scale(var(--scale))] data-ending-style:transform-[translateY(calc(100%-var(--drawer-bleed)+2px))] data-starting-style:transform-[translateY(calc(100%-var(--drawer-bleed)+2px))] max-h-[calc(100dvh-1rem+var(--drawer-bleed))] origin-[50%_calc(100%-var(--drawer-bleed))] rounded-t-3xl pb-[calc(env(safe-area-inset-bottom,0px)+var(--drawer-bleed))] [--drawer-bleed:3rem] [--drawer-peek:1rem] [--scale-base:calc(max(0,1-(var(--nested-drawers)*var(--stack-step))))] [--scale:clamp(0,calc(var(--scale-base)+(var(--stack-step)*var(--stack-progress))),1)] [--shrink:calc(1-var(--scale))] [--stack-height:max(0px,calc(var(--drawer-frontmost-height,var(--drawer-height))-var(--drawer-bleed)))] [--stack-peek-offset:max(0px,calc((var(--nested-drawers)-var(--stack-progress))*var(--drawer-peek)))] [--stack-progress:clamp(0,var(--drawer-swipe-progress),1)] [--stack-step:0.05] after:pointer-events-none after:absolute after:inset-0 after:bg-transparent after:transition-colors after:duration-450 after:ease-[cubic-bezier(0.32,0.72,0,1)] after:content-[''] data-ending-style:data-nested-drawer-swiping:duration-[calc(var(--drawer-swipe-strength)*450ms)] data-nested-drawer-open:h-[calc(var(--stack-height)+var(--drawer-bleed))] data-nested-drawer-open:overflow-hidden data-nested-drawer-swiping:duration-0 data-nested-drawer-open:after:bg-black/5",
              preserveHeight
                ? "h-[calc(100dvh-1rem+var(--drawer-bleed))]"
                : "h-(--drawer-height,auto)",

              // Desktop (side drawer)
              "xl:data-ending-style:transform-[translateX(calc(100%+var(--viewport-padding)+2px))] xl:data-starting-style:transform-[translateX(calc(100%+var(--viewport-padding)+2px))] xl:transform-[translateX(var(--drawer-swipe-movement-x))] xl:mb-0 xl:h-full xl:max-h-none xl:w-screen xl:max-w-md xl:rounded-3xl xl:pb-0 xl:transition-transform"
            )}
          >
            <PortalContainerProvider container={portalContainer}>
              {/* Bottom sheet handle */}
              <div className="mx-auto my-4 h-1 w-9 shrink-0 rounded-full bg-gray-300 transition-opacity duration-200 group-data-nested-drawer-open/popup:opacity-0 group-data-nested-drawer-swiping/popup:opacity-100 xl:hidden dark:bg-gray-600" />

              <BaseDrawer.Content
                className={clsx(
                  "mx-auto flex size-full min-h-0 flex-col transition-opacity duration-300 ease-[cubic-bezier(0.45,1.005,0,1.005)] group-data-nested-drawer-swiping/popup:opacity-100 lg:mx-0 lg:max-w-none",
                  hideContentOnNestedDrawer &&
                    "group-data-nested-drawer-open/popup:opacity-0"
                )}
              >
                <div
                  className={clsx(
                    "border-b px-4 transition-colors sm:px-6 xl:py-6",
                    header && "pb-4",
                    isScrolled
                      ? "border-gray-200 dark:border-white/10"
                      : "border-transparent"
                  )}
                >
                  <div
                    className={clsx(
                      "relative flex w-full items-start justify-between gap-3",
                      !fullWidthContent && "mx-auto max-w-[464px]"
                    )}
                  >
                    {header}
                    <BaseDrawer.Close
                      render={({ children, ...props }) => (
                        <Button
                          {...props}
                          variant="transparent"
                          disabled={!dismissible}
                          className="-mt-1.5 -mr-1.5 hidden! xl:flex! size-10!"
                        >
                          <XMarkIcon className="size-5" />
                          <span className="sr-only">Close</span>
                        </Button>
                      )}
                    />
                  </div>
                </div>

                <div
                  ref={scrollContainerRef}
                  onScroll={handleScroll}
                  className="flex-1 overflow-y-auto px-4 pb-4 sm:px-6 xl:pb-6 dark:[scrollbar-color:var(--color-gray-700)_var(--color-gray-850)]"
                >
                  <div
                    className={clsx(
                      "relative w-full",
                      !fullWidthContent && "mx-auto max-w-[464px]"
                    )}
                  >
                    {children}
                  </div>
                </div>
              </BaseDrawer.Content>
            </PortalContainerProvider>
          </BaseDrawer.Popup>
        </BaseDrawer.Viewport>
      </BaseDrawer.Portal>
    </BaseDrawer.Root>
  )
}

const Title = (props: ComponentProps<typeof BaseDrawer.Title>) => (
  <BaseDrawer.Title {...props} />
)

Drawer.Title = Title

export default Drawer
